import React, { useMemo, useState } from 'react';
import {
  Accordion,
  Badge,
  Button,
  Group,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { HiArrowUpRight } from 'react-icons/hi2';
import { AutomationStep } from '@/types/automationSteps.ts';
import {
  collectStepConnections,
  StepConnection,
} from '@/utils/stepConnections.ts';

type Props = {
  steps: AutomationStep[] | undefined;
  stepId: string;
  onSelectStep: (stepId: string) => void;
};

function ConnectionRow({
  connection,
  onSelectStep,
}: {
  connection: StepConnection;
  onSelectStep: (stepId: string) => void;
}) {
  const clickable = !!connection.stepId;

  const content = (
    <Group
      wrap="nowrap"
      align="center"
      justify="space-between"
      gap="xs"
      px={10}
      py={8}
      style={{
        border: '1px solid #ECECED',
        borderRadius: '6px',
        background: clickable ? '#FDFDFD' : '#FAFAFA',
      }}
    >
      <Group gap="xs" wrap="nowrap" style={{ minWidth: 0, flex: 1 }}>
        <Stack gap={1} style={{ minWidth: 0, flex: 1 }}>
          <Text size="12px" fw={600} lh="16px" truncate title={connection.displayName}>
            {connection.displayName}
          </Text>
          <Text size="11px" lh="14px" c="dimmed" truncate title={connection.detail}>
            {connection.detail}
          </Text>
        </Stack>
      </Group>
      {clickable && <HiArrowUpRight size={14} color="#757575" style={{ flexShrink: 0 }} />}
    </Group>
  );

  if (!clickable || !connection.stepId) {
    return content;
  }

  return (
    <UnstyledButton w="100%" onClick={() => onSelectStep(connection.stepId!)}>
      {content}
    </UnstyledButton>
  );
}

function ConnectionSection({
  title,
  emptyText,
  connections,
  onSelectStep,
}: {
  title: string;
  emptyText: string;
  connections: StepConnection[];
  onSelectStep: (stepId: string) => void;
}) {
  const [showAll, setShowAll] = useState(false);
  const visibleConnections = showAll ? connections : connections.slice(0, MAX_VISIBLE_CONNECTIONS);
  const hiddenConnectionCount = connections.length - visibleConnections.length;

  return (
    <Stack gap={6}>
      <Group justify="space-between" wrap="nowrap">
        <Text size="11px" fw={600} c="dimmed" tt="uppercase">
          {title}
        </Text>
        <Badge size="xs" variant="light" color={connections.length > 0 ? 'blue' : 'gray'}>
          {connections.length}
        </Badge>
      </Group>
      {connections.length === 0 ? (
        <Text size="12px" c="dimmed">
          {emptyText}
        </Text>
      ) : (
        <Stack gap={6}>
          {visibleConnections.map((connection) => (
            <ConnectionRow
              key={connection.id}
              connection={connection}
              onSelectStep={onSelectStep}
            />
          ))}
          {hiddenConnectionCount > 0 && (
            <Button
              variant="subtle"
              color="gray"
              size="compact-xs"
              onClick={() => setShowAll(true)}
              style={{ alignSelf: 'flex-start' }}
            >
              Show {hiddenConnectionCount} more
            </Button>
          )}
        </Stack>
      )}
    </Stack>
  );
}

const MAX_VISIBLE_CONNECTIONS = 5;
const REFERENCES_VALUE = 'references';

const StepConnectionsPanel = ({ steps, stepId, onSelectStep }: Props) => {
  const [referencesValue, setReferencesValue] = useState<string | null>(null);
  const connections = useMemo(
    () => collectStepConnections(steps, stepId),
    [steps, stepId],
  );

  const totalConnectionCount = connections.uses.length + connections.referencedBy.length;

  return (
    <Accordion value={referencesValue} onChange={setReferencesValue} variant="contained">
      <Accordion.Item value={REFERENCES_VALUE}>
        <Accordion.Control>
          <Group gap={8} wrap="nowrap" justify="space-between">
            <Text size="13px" fw={600}>
              References
            </Text>
            <Badge
              size="xs"
              color={totalConnectionCount > 0 ? 'blue' : 'gray'}
              variant="light"
              mr={4}
            >
              {totalConnectionCount}
            </Badge>
          </Group>
        </Accordion.Control>
        <Accordion.Panel>
          <Stack gap="sm">
            <ConnectionSection
              title="This step uses"
              emptyText="This step does not use another step or saved file directly."
              connections={connections.uses}
              onSelectStep={onSelectStep}
            />
            <ConnectionSection
              title="Used by later steps"
              emptyText="No later steps directly depend on this step."
              connections={connections.referencedBy}
              onSelectStep={onSelectStep}
            />
          </Stack>
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
};

export default StepConnectionsPanel;
